import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import { Loader2, Plus, X, FileText, ExternalLink, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/api";

/* Admin → Pages: manage the public legal/content pages (Privacy Policy,
   Terms & Conditions, …). Published pages are served at /p/<slug> and linked
   from the landing footer automatically. */
export default function AdminSitePages() {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null);   // page object or "new"
  const [busySlug, setBusySlug] = useState(null);

  const load = () => apiClient.get("/admin/pages").then(({ data }) => setPages(data.pages)).catch(() => setPages([]));
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!(await confirm({ title: "Please confirm", description: `Delete "${p.title}"? The public /p/${p.slug} link stops working immediately.`, danger: true }))) return;
    setBusySlug(p.slug);
    try { await apiClient.delete(`/admin/pages/${p.slug}`); load(); }
    finally { setBusySlug(null); }
  };

  if (pages === null) return <p className="text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>;

  return (
    <div data-testid="admin-pages">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-stone-100">Pages</h1>
          <p className="text-sm text-stone-400 mt-1">Public legal & content pages — Privacy Policy, Terms and more. Published pages appear in the site footer at /p/&lt;slug&gt;.</p>
        </div>
        <button onClick={() => setEditing("new")} data-testid="pages-add"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412]">
          <Plus size={15} /> New page
        </button>
      </div>

      {editing && <PageEditorModal page={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <div className="mt-8 space-y-3">
        {pages.length === 0 && <p className="text-sm text-stone-500">No pages yet — click "New page".</p>}
        {pages.map((p) => (
          <div key={p.slug} className="rounded-2xl border border-stone-800 bg-[#1c1917] p-5 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-100 flex items-center gap-2">
                <FileText size={14} className="text-stone-500" /> {p.title}
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${p.published ? "bg-emerald-900/50 text-emerald-400" : "bg-stone-800 text-stone-500"}`}>
                  {p.published ? "published" : "draft"}
                </span>
              </p>
              <p className="text-xs text-stone-500 mt-1 font-mono">/p/{p.slug} · updated {p.updated_at ? new Date(p.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</p>
            </div>
            <div className="flex items-center gap-2">
              {p.published && (
                <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer"
                  className="text-xs font-medium px-3 py-1.5 rounded-full border border-stone-700 text-stone-300 hover:border-stone-400 inline-flex items-center gap-1">
                  View <ExternalLink size={11} />
                </a>
              )}
              <button onClick={() => setEditing(p)} data-testid={`pages-edit-${p.slug}`}
                className="text-xs font-medium px-3 py-1.5 rounded-full border border-stone-700 text-stone-300 hover:border-stone-400">Edit</button>
              <button onClick={() => del(p)} disabled={busySlug === p.slug}
                className="text-xs font-medium px-3 py-1.5 rounded-full border border-red-900 text-red-400 hover:bg-red-950 disabled:opacity-50 inline-flex items-center gap-1">
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs text-stone-500">Formatting: start a line with <span className="font-mono text-stone-400">## </span> for a section heading; blank lines separate paragraphs. Plain text only.</p>
    </div>
  );
}

function PageEditorModal({ page, onClose, onSaved }) {
  const isNew = !page;
  const [form, setForm] = useState({
    slug: page?.slug || "",
    title: page?.title || "",
    body: page?.body || "",
    published: page?.published || false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const payload = { title: form.title, body: form.body, published: form.published };
      if (isNew) await apiClient.post("/admin/pages", { ...payload, slug: form.slug.trim().toLowerCase() });
      else await apiClient.put(`/admin/pages/${page.slug}`, payload);
      onSaved?.();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Failed to save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl rounded-2xl bg-[#1c1917] border border-stone-800 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 shrink-0">
          <h2 className="text-lg font-medium text-stone-100">{isNew ? "New page" : `Edit — ${page.title}`}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-800"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="px-6 py-5 space-y-4 overflow-y-auto">
          {isNew && (
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-stone-500">Slug (URL: /p/&lt;slug&gt;)</span>
              <input value={form.slug} onChange={set("slug")} placeholder="cookie-policy" data-testid="page-slug"
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-100 font-mono focus:border-[#C2410C] outline-none" />
            </label>
          )}
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-stone-500">Title</span>
            <input value={form.title} onChange={set("title")} placeholder="Privacy Policy" data-testid="page-title"
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-100 focus:border-[#C2410C] outline-none" />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-stone-500">Content — "## " starts a heading, blank line = new paragraph</span>
            <textarea value={form.body} onChange={set("body")} rows={14} data-testid="page-body"
              placeholder={"## 1. Information we collect\nWe collect the information you give us when you create an account…"}
              className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl bg-stone-900 border border-stone-700 text-sm text-stone-100 leading-relaxed focus:border-[#C2410C] outline-none resize-y" />
          </label>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <button type="button" onClick={() => setForm((f) => ({ ...f, published: !f.published }))} data-testid="page-published"
              className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${form.published ? "bg-emerald-600" : "bg-stone-700"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.published ? "left-[26px]" : "left-0.5"}`} />
            </button>
            <span className="text-sm text-stone-300">{form.published ? "Published — live at the public link and in the footer" : "Draft — not visible to anyone"}</span>
          </label>
          {err && <p className="text-sm text-red-400">{err}</p>}
          <div className="flex justify-end gap-2 pb-1">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-full border border-stone-700 text-sm text-stone-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="page-save"
              className="px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60 inline-flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
